import { useState } from 'react';
import { toast } from 'sonner';

import { updateWorkflowStatus } from '@/hooks/workflow';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const WorkflowStatusToggle = ({ workflow, onStatusChange, onClose }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(workflow.is_active);

  const handleToggleStatus = async () => {
    try {
      setIsLoading(true);

      await updateWorkflowStatus(workflow._id, { is_active: selectedStatus });

      // Notify success
      toast.success(
        `Workflow status ${selectedStatus ? 'activated' : 'deactivated'} successfully`
      );

      // Close dialog and refresh data
      handleClose();

      // Call the callback to refresh parent components
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error) {
      console.error('Error updating workflow status:', error);
      toast.error('Failed to update workflow status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Change Workflow Status</DialogTitle>
          <DialogDescription>
            Update the status of &ldquo;{workflow.name}&rdquo; workflow.
          </DialogDescription>
        </DialogHeader>

        <div className='py-4'>
          <h3 className='mb-1 font-medium'>Workflow Details</h3>
          <p className='mb-4 text-sm text-muted-foreground'>
            {workflow.description || 'No description provided'}
          </p>

          <div className='space-y-4'>
            <RadioGroup
              value={selectedStatus.toString()}
              onValueChange={(value) => setSelectedStatus(value === 'true')}
              className='flex flex-col space-y-2'
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='true' id='active' />
                <Label htmlFor='active'>Active</Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='false' id='inactive' />
                <Label htmlFor='inactive'>Inactive</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleToggleStatus} disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowStatusToggle;
